<template>
  <header class="portal-header" :class="{ scrolled: isScrolled }">
    <div class="header-inner">
      <router-link to="/" class="logo">
        <img v-if="siteLogo && !logoError" :src="siteLogo" :alt="siteName" class="logo-img" @error="logoError = true" />
        <span v-if="siteLogo && !logoError && siteName" class="logo-divider"></span>
        <span v-if="siteName" class="logo-text logo-full">{{ siteName }}</span>
        <template v-if="!siteLogo && !siteName">
          <span class="logo-text">ENERGY</span>
          <span class="logo-accent">CORP</span>
        </template>
      </router-link>

      <nav class="nav-desktop">
        <router-link to="/" exact>{{ $t('nav.home') }}</router-link>
        <router-link to="/products">{{ $t('nav.products') }}</router-link>
        <router-link to="/solutions">{{ $t('nav.solutions') }}</router-link>
        <router-link to="/cases">{{ $t('nav.cases') }}</router-link>
        <router-link to="/news">{{ $t('nav.news') }}</router-link>
        <router-link to="/about">{{ $t('nav.about') }}</router-link>
        <router-link to="/contact">{{ $t('nav.contact') }}</router-link>
        <router-link to="/downloads">{{ $t('nav.downloads') }}</router-link>
      </nav>

      <div class="header-right">
        <div class="lang-switch">
          <span v-for="l in languages" :key="l.code" :class="{ active: currentLang === l.code }" @click="switchLang(l.code)">
            {{ l.label }}
          </span>
        </div>
        <div class="mobile-toggle" @click="mobileOpen = !mobileOpen">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <transition name="slide">
      <div v-if="mobileOpen" class="nav-mobile">
        <router-link to="/" @click.native="mobileOpen = false">{{ $t('nav.home') }}</router-link>
        <router-link to="/products" @click.native="mobileOpen = false">{{ $t('nav.products') }}</router-link>
        <router-link to="/solutions" @click.native="mobileOpen = false">{{ $t('nav.solutions') }}</router-link>
        <router-link to="/cases" @click.native="mobileOpen = false">{{ $t('nav.cases') }}</router-link>
        <router-link to="/news" @click.native="mobileOpen = false">{{ $t('nav.news') }}</router-link>
        <router-link to="/about" @click.native="mobileOpen = false">{{ $t('nav.about') }}</router-link>
        <router-link to="/contact" @click.native="mobileOpen = false">{{ $t('nav.contact') }}</router-link>
        <router-link to="/downloads" @click.native="mobileOpen = false">{{ $t('nav.downloads') }}</router-link>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  data() {
    return {
      isScrolled: false,
      mobileOpen: false,
      logoError: false,
      languages: [
        { code: 'zh', label: '中' },
        { code: 'en', label: 'EN' },
        { code: 'ru', label: 'RU' }
      ]
    }
  },
  computed: {
    currentLang() { return this.$store.state.lang },
    siteLogo() { return this.$store.state.siteConfig.siteLogo || '' },
    siteName() { return this.$store.state.siteConfig.siteName || '' }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() { this.isScrolled = window.scrollY > 50 },
    switchLang(lang) {
      this.$i18n.locale = lang
      this.$store.dispatch('setLang', lang)
      document.documentElement.lang = lang
    }
  }
}
</script>

<style lang="scss" scoped>
$gold: var(--color-gold);
$dark: var(--color-primary);

.portal-header {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  transition: all 0.4s ease;
  background: transparent;

  &.scrolled {
    background: rgba(10, 10, 26, 0.95);
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 20px rgba(0,0,0,0.3);
  }
}

.header-inner {
  max-width: var(--container-width); margin: 0 auto; padding: 0 var(--container-padding);
  display: flex; align-items: center; justify-content: space-between;
  height: var(--header-height);
}

.logo {
  display: flex; align-items: center; gap: 16px;
  .logo-text { font-size: 24px; font-weight: 700; color: #fff; letter-spacing: 3px; font-family: 'Montserrat', sans-serif; }
  .logo-accent { font-size: 24px; font-weight: 300; color: $gold; letter-spacing: 3px; font-family: 'Montserrat', sans-serif; }
  .logo-full {
    font-family: 'Montserrat', sans-serif;
    font-size: 18px; font-weight: 600; color: #fff; letter-spacing: 2px;
    white-space: nowrap; line-height: 1.3;
  }
  .logo-divider {
    width: 1px; height: 28px; background: linear-gradient(180deg, transparent, $gold, transparent);
    flex-shrink: 0;
  }
  .logo-img {
    max-height: 48px; max-width: 160px; width: auto; height: auto;
    object-fit: contain; display: block; flex-shrink: 0;
  }
}

.nav-desktop {
  display: flex; gap: 32px;
  a {
    font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.8); text-transform: uppercase;
    letter-spacing: 1.5px; transition: color 0.3s; position: relative; padding: 8px 0;
    font-family: 'Montserrat', sans-serif;
    &::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 2px; background: $gold; transition: width 0.3s; }
    &:hover, &.router-link-active { color: $gold; &::after { width: 100%; } }
  }
}

.header-right { display: flex; align-items: center; gap: 20px; }

.lang-switch {
  display: flex; gap: 8px;
  span {
    font-size: 12px; color: rgba(255,255,255,0.5); cursor: pointer; padding: 4px 8px;
    border: 1px solid rgba(255,255,255,0.2); border-radius: 3px; transition: all 0.3s;
    font-family: 'Montserrat', sans-serif;
    &:hover, &.active { color: $gold; border-color: $gold; }
  }
}

.mobile-toggle {
  display: none; flex-direction: column; gap: 5px; cursor: pointer;
  span { width: 24px; height: 2px; background: #fff; transition: all 0.3s; }
}

.nav-mobile {
  background: rgba(10, 10, 26, 0.98); padding: 20px 40px;
  display: flex; flex-direction: column; gap: 16px;
  a { color: rgba(255,255,255,0.8); font-size: 14px; text-transform: uppercase; letter-spacing: 1px; }
}

.slide-enter-active, .slide-leave-active { transition: all 0.3s ease; }
.slide-enter, .slide-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 1024px) {
  .nav-desktop { display: none; }
  .mobile-toggle { display: flex; }
}
@media (max-width: 480px) {
  .header-right { gap: 12px; }
  .lang-switch {
    gap: 6px;
    span { padding: 2px 6px; font-size: 11px; }
  }
}
</style>
